import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Session } from "@supabase/supabase-js";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "sonner";
import { Loader2, LogOut, KeyRound, RefreshCw, AlertTriangle, CalendarIcon } from "lucide-react";

type VariantStat = {
  variant: string;
  views: number;
  clicks: number;
  checkoutClicks: number;
  avgTimeSeconds: number;
  checkoutCtr: number;
};

const VARIANTS = ["A", "B", "C", "D"] as const;

const formatDuration = (seconds: number): string => {
  if (!seconds || seconds <= 0) return "—";
  const total = Math.round(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  if (m === 0) return `${s}s`;
  return `${m}m ${s.toString().padStart(2, "0")}s`;
};

const Dashboard = () => {
  const [session, setSession] = useState<Session | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [signingIn, setSigningIn] = useState(false);

  const [stats, setStats] = useState<VariantStat[]>([]);
  const [loadingStats, setLoadingStats] = useState(false);
  // Filtro de data: undefined = todos os dias
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());

  const [newPassword, setNewPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
    });
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setAuthLoading(false);
    });
    return () => subscription.unsubscribe();
  }, []);

  const fetchStats = async () => {
    setLoadingStats(true);
    try {
      let query = supabase
        .from("ab_events")
        .select("variant, event_type, duration_seconds")
        .limit(100000);
      if (selectedDate) {
        const start = new Date(selectedDate);
        start.setHours(0, 0, 0, 0);
        const end = new Date(selectedDate);
        end.setHours(23, 59, 59, 999);
        query = query.gte("created_at", start.toISOString()).lte("created_at", end.toISOString());
      }
      const { data, error } = await query;
      if (error) throw error;

      const counts: Record<
        string,
        { views: number; clicks: number; checkoutClicks: number; timeSum: number; timeCount: number }
      > = {};
      VARIANTS.forEach(
        (v) => (counts[v] = { views: 0, clicks: 0, checkoutClicks: 0, timeSum: 0, timeCount: 0 }),
      );
      (data ?? []).forEach((row) => {
        if (!counts[row.variant])
          counts[row.variant] = { views: 0, clicks: 0, checkoutClicks: 0, timeSum: 0, timeCount: 0 };
        if (row.event_type === "view") counts[row.variant].views++;
        else if (row.event_type === "click") counts[row.variant].clicks++;
        else if (row.event_type === "checkout_click") counts[row.variant].checkoutClicks++;
        else if (row.event_type === "time_on_page" && typeof row.duration_seconds === "number") {
          counts[row.variant].timeSum += row.duration_seconds;
          counts[row.variant].timeCount += 1;
        }
      });

      const result: VariantStat[] = VARIANTS.map((v) => {
        const c = counts[v];
        const checkoutCtr = c.views > 0 ? (c.checkoutClicks / c.views) * 100 : 0;
        const avgTimeSeconds = c.timeCount > 0 ? c.timeSum / c.timeCount : 0;
        return {
          variant: v,
          views: c.views,
          clicks: c.clicks,
          checkoutClicks: c.checkoutClicks,
          avgTimeSeconds,
          checkoutCtr,
        };
      });
      setStats(result);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Erro ao carregar dados";
      toast.error(msg);
    } finally {
      setLoadingStats(false);
    }
  };

  useEffect(() => {
    if (session) fetchStats();
  }, [session, selectedDate]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setSigningIn(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setSigningIn(false);
    if (error) toast.error(error.message);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    toast.success("Sessão encerrada");
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error("A senha deve ter pelo menos 8 caracteres");
      return;
    }
    setChangingPassword(true);
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    setChangingPassword(false);
    if (error) {
      toast.error(error.message);
    } else {
      // Remove o flag de senha temporária
      try {
        await supabase.auth.updateUser({ data: { temp_password: false } });
      } catch {
        // ignore
      }
      toast.success("Senha alterada com sucesso");
      setNewPassword("");
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Dashboard A/B</CardTitle>
            <CardDescription>Acesso restrito</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSignIn} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Senha</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
              </div>
              <Button type="submit" className="w-full" disabled={signingIn}>
                {signingIn ? <Loader2 className="w-4 h-4 animate-spin" /> : "Entrar"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  const totalViews = stats.reduce((s, x) => s + x.views, 0);
  const totalClicks = stats.reduce((s, x) => s + x.clicks, 0);
  const totalCheckoutClicks = stats.reduce((s, x) => s + x.checkoutClicks, 0);
  const overallCheckoutCtr = totalViews > 0 ? (totalCheckoutClicks / totalViews) * 100 : 0;
  // Média global de tempo, ponderada pelas amostras de cada variante
  const totalTimeSamples = stats.filter((s) => s.avgTimeSeconds > 0);
  const overallAvgTime =
    totalTimeSamples.length > 0
      ? totalTimeSamples.reduce((s, x) => s + x.avgTimeSeconds, 0) / totalTimeSamples.length
      : 0;
  // Melhor variante baseada no clique de checkout (intenção de compra real)
  const bestVariant = stats.reduce(
    (best, cur) => (cur.checkoutCtr > (best?.checkoutCtr ?? -1) ? cur : best),
    null as VariantStat | null,
  );
  const hasTempPassword = Boolean(session.user.user_metadata?.temp_password);

  const scrollToChangePassword = () => {
    const el = document.getElementById("change-password");
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      const input = el.querySelector("input");
      if (input) (input as HTMLInputElement).focus();
    }
  };

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="max-w-5xl mx-auto space-y-6">
        {hasTempPassword && (
          <Alert variant="destructive" className="border-2">
            <AlertTriangle className="h-5 w-5" />
            <AlertTitle>Você está usando uma senha temporária</AlertTitle>
            <AlertDescription className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mt-2">
              <span>Por segurança, troque agora para uma senha pessoal.</span>
              <Button size="sm" variant="outline" onClick={scrollToChangePassword}>
                Trocar senha agora
              </Button>
            </AlertDescription>
          </Alert>
        )}

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-black text-foreground">Dashboard A/B Test</h1>
            <p className="text-muted-foreground text-sm">{session.user.email}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className={cn("justify-start text-left font-normal", !selectedDate && "text-muted-foreground")}>
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  {selectedDate ? format(selectedDate, "dd 'de' MMM, yyyy", { locale: ptBR }) : "Todos os dias"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  locale={ptBR}
                  initialFocus
                  className={cn("p-3 pointer-events-auto")}
                />
                <div className="p-2 border-t">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full"
                    onClick={() => setSelectedDate(undefined)}
                  >
                    Mostrar todos os dias
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
            <Button variant="outline" size="sm" onClick={fetchStats} disabled={loadingStats}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loadingStats ? "animate-spin" : ""}`} />
              Atualizar
            </Button>
            <Button variant="outline" size="sm" onClick={handleSignOut}>
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Visualizações totais</CardDescription>
              <CardTitle className="text-3xl">{totalViews.toLocaleString("pt-BR")}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Tempo médio na página</CardDescription>
              <CardTitle className="text-3xl">{formatDuration(overallAvgTime)}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Cliques Checkout</CardDescription>
              <CardTitle className="text-3xl text-[hsl(var(--cta))]">
                {totalCheckoutClicks.toLocaleString("pt-BR")}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>CTR Checkout</CardDescription>
              <CardTitle className="text-3xl text-[hsl(var(--cta))]">
                {overallCheckoutCtr.toFixed(2)}%
              </CardTitle>
            </CardHeader>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Performance por Variante</CardTitle>
            <CardDescription>
              {bestVariant && bestVariant.views > 0
                ? `Melhor variante (por checkout): ${bestVariant.variant} (${bestVariant.checkoutCtr.toFixed(2)}% CTR Checkout)`
                : "Sem dados ainda"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b">
                  <tr className="text-left text-muted-foreground">
                    <th className="py-2 pr-4">Variante</th>
                    <th className="py-2 pr-4 text-right">Views</th>
                    <th className="py-2 pr-4 text-right">Cliques CTA</th>
                    <th className="py-2 pr-4 text-right">Tempo médio</th>
                    <th className="py-2 pr-4 text-right">Checkout</th>
                    <th className="py-2 text-right">CTR Checkout</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.map((s) => {
                    const isBest = bestVariant?.variant === s.variant && s.views > 0;
                    return (
                      <tr key={s.variant} className="border-b last:border-0">
                        <td className="py-3 pr-4 font-bold">
                          {s.variant}
                          {isBest && <span className="ml-2 text-xs text-primary">★ melhor</span>}
                        </td>
                        <td className="py-3 pr-4 text-right">{s.views.toLocaleString("pt-BR")}</td>
                        <td className="py-3 pr-4 text-right">{s.clicks.toLocaleString("pt-BR")}</td>
                        <td className="py-3 pr-4 text-right">{formatDuration(s.avgTimeSeconds)}</td>
                        <td className="py-3 pr-4 text-right font-semibold text-[hsl(var(--cta))]">
                          {s.checkoutClicks.toLocaleString("pt-BR")}
                        </td>
                        <td className="py-3 text-right font-bold text-[hsl(var(--cta))]">
                          {s.checkoutCtr.toFixed(2)}%
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground mt-4">
              A: Headline original • B: "Em 4h ao vivo, fechar como Closer de ponta" • C: VSL com headline A • D: VSL com headline B
              <br />
              <strong>Cliques CTA</strong>: botões intermediários que rolam para a oferta. <strong>Tempo médio</strong>: tempo médio de visualização por usuário (sessão). <strong>Checkout</strong>: botão final que abre o pagamento (intenção real de compra).
            </p>
          </CardContent>
        </Card>

        <Card id="change-password">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5" />
              Alterar Senha
            </CardTitle>
            <CardDescription>Mínimo 8 caracteres</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleChangePassword} className="flex flex-col md:flex-row gap-3 max-w-md">
              <Input
                type="password"
                placeholder="Nova senha"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                required
                minLength={8}
              />
              <Button type="submit" disabled={changingPassword}>
                {changingPassword ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salvar"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
